import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Show } from "../types/Show";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { renderSubTitleIfValid } from "../utils/subTitleUtils";
import OptimizedShowImage from "./common/OptimizedShowImage";
import { useImagePreloader } from "../hooks/ui/useImagePreloader";
import { isMobileDevice, trackImagePerformance } from "../utils/imageUtils";
import logoImage from "../assets/logos/e-logo-trans-40.png";
import fallbackShowImage from "../assets/images/e-logo-600-min.png";
import { TouchPoint } from "../hooks/ui/useScrollAwareTap";

interface ShowTileProps {
  show: Show;
  isNewShow?: boolean;
  index?: number; // For priority loading
  isAboveFold?: boolean; // For priority loading
  disableNavigation?: boolean; // For carousel usage where navigation is handled externally
  tileSizePx?: number;
}

const ShowTile: React.FC<ShowTileProps> = ({ 
  show, 
  isNewShow = false, 
  index = 0,
  isAboveFold = false,
  disableNavigation = false,
  tileSizePx = 224
}) => {
  const { playShow, currentTrack, isPlaying } = useAudioPlayer();
  const { preloadImage, getPreloadStats } = useImagePreloader();
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const touchStartRef = useRef<TouchPoint | null>(null);
  const hasTouchMovedRef = useRef(false);
  const tapCancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle responsive image sizing
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Preload larger image on hover for better UX (desktop only)
  const handleMouseEnter = () => {
    if (!isMobile) {
      const largeImage =
        show.images['400x400'] || show.images['300x300'] || show.images['600x600'];
      if (largeImage) {
        preloadImage(largeImage, false); // Non-priority preload
      }
    }
  };

  // Performance tracking callback
  const handleImageLoadComplete = (loadTime: number) => {
    const stats = getPreloadStats();
    trackImagePerformance({
      loadTime,
      size: imageSize,
      isMobile,
      cacheHit: stats.cacheSize > 0 // Consider cache hit if we have cached images
    });
  };

  const formatDateTime = (dateTimeString: string) => {
    const [date, time] = dateTimeString.split("#");
    const dateObj = new Date(`${date}T${time}`);
    return {
      date: dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      }),
      time: dateObj.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  };

  const handlePlayShow = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (show.audioUrl) {
      playShow(show);
    }
  };

  const { date, time } = formatDateTime(show.date_start_time);

  // Check if this show is currently playing
  const isCurrentlyPlaying =
    currentTrack?.show?.show === show.show &&
    currentTrack?.show?.date_start_time === show.date_start_time &&
    isPlaying;

  // Determine if this image should be loaded with priority
  const shouldUsePriority = isAboveFold || index < 3;
  const imageSize = tileSizePx <= 200 ? 'small' : 'medium';
  const preferredVariants = tileSizePx <= 200
    ? ['300x300', '150x150', '400x400']
    : ['400x400', '300x300', '150x150'];

  // Common className for the container
  const containerClassName = `relative block aspect-square rounded-xl sm:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
    isNewShow ? "ring-2 ring-green-500 ring-opacity-50" : ""
  }`;

  // Container element - either Link or div based on disableNavigation
  const linkPath = `/show/${show.show_id}`;

  const resetTouchTracking = useCallback(() => {
    touchStartRef.current = null;
    hasTouchMovedRef.current = false;
    if (tapCancelTimeoutRef.current) {
      clearTimeout(tapCancelTimeoutRef.current);
      tapCancelTimeoutRef.current = null;
    }
  }, []);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    hasTouchMovedRef.current = false;
  }, []);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    const start = touchStartRef.current;
    if (!touch || !start) return;

    const deltaX = Math.abs(touch.clientX - start.x);
    const deltaY = Math.abs(touch.clientY - start.y);
    if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) > 12) {
      hasTouchMovedRef.current = true;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    tapCancelTimeoutRef.current = setTimeout(() => {
      resetTouchTracking();
    }, 0);
  }, [resetTouchTracking]);

  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    if (hasTouchMovedRef.current) {
      event.preventDefault();
      event.stopPropagation();
      resetTouchTracking();
    }
  }, [resetTouchTracking]);

  useEffect(() => {
    return () => {
      if (tapCancelTimeoutRef.current) {
        clearTimeout(tapCancelTimeoutRef.current);
        tapCancelTimeoutRef.current = null;
      }
    };
  }, []);

  const renderContainer = () => {
    const content = (
      <>
        <div className="absolute inset-0">
          <OptimizedShowImage
            show={show}
            size={imageSize}
            className="absolute inset-0"
            alt={`${show.show} - ${show.presenter?.name || 'Unknown Presenter'}`}
            priority={shouldUsePriority}
            preload={shouldUsePriority}
            preconnect={shouldUsePriority}
            sizes={`${tileSizePx}px`}
            preferredVariants={preferredVariants}
            onLoadComplete={handleImageLoadComplete}
            fallbackSrc={fallbackShowImage}
          />
        </div>

        {/* Logo in top left with subtle gradient background */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-2 sm:p-4 z-20 flex">
          <img
            src={logoImage}
            alt="Eruption Radio"
            className="w-5 h-5 sm:w-6 sm:h-6 object-contain opacity-90 hover:opacity-100 transition-opacity"
            loading="lazy"
          />
        </div>

        {/* Live indicator - moved to right side */}
        {show.isLive && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-20">
            <div className="bg-red-800 text-white text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full flex items-center">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full mr-1 animate-pulse"></div>
              <span className="hidden sm:inline">LIVE NOW</span>
              <span className="sm:hidden">LIVE</span>
            </div>
          </div>
        )}

        {/* Play button - hidden on mobile, shows on desktop hover */}
        {!show.isLive && show.audioUrl && (
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-20 hidden sm:block">
            <div
              onClick={handlePlayShow}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg opacity-0 group-hover:opacity-100 cursor-pointer
                ${
                  isCurrentlyPlaying
                    ? "bg-emerald-800 hover:bg-emerald-900"
                    : "bg-red-800 hover:bg-red-900"
                }
              `}
              role="presentation"
              tabIndex={-1}
              aria-hidden="true"
              title={isCurrentlyPlaying ? "Currently Playing" : "Play Show"}
            >
              {isCurrentlyPlaying ? (
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 text-white ml-0.5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </div>
          </div>
        )}

        {/* Navigation hint - shows on hover (desktop only) */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center hover:pointer-events-auto pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 hidden sm:block">
            <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1.5 rounded-full text-sm font-medium shadow-lg flex items-center">
              <span>View Details</span>
              <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Always visible text overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 sm:p-4">
          <h3 className="text-white font-bold text-sm sm:text-lg mb-0.5 sm:mb-1 leading-tight">
            {show.show}
          </h3>
          {/* Sub-title with graceful null/empty handling */}
          {renderSubTitleIfValid(show.sub_title, (validSubTitle) => (
            <p className="text-white text-xs sm:text-sm leading-tight font-medium">
              {validSubTitle}
            </p>
          ))}
        </div>
      </>
    );

    if (disableNavigation) {
      return (
        <div
          className={containerClassName}
          onMouseEnter={handleMouseEnter}
          style={{
            touchAction: 'pan-x pan-y',
            userSelect: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          {content}
        </div>
      );
    } else {
      return (
        <Link
          to={linkPath}
          className={containerClassName}
          onMouseEnter={handleMouseEnter}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClickCapture={handleClickCapture}
        >
          {content}
        </Link>
      );
    }
  };

  return (
    <div className={`group ${isNewShow ? "animate-slide-in-bounce" : ""}`}>
      {/* New show indicator */}
      {isNewShow && (
        <div className="absolute -top-2 -right-2 z-30">
          <div className="bg-emerald-800 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center animate-pulse">
            <div className="w-2 h-2 bg-white rounded-full mr-1 animate-ping"></div>
            <span>NEW</span>
          </div>
        </div>
      )}

      {renderContainer()}

      {/* Date and time below the tile */}
      <div className="mt-1 sm:mt-2 text-center">
        <p className="text-gray-200 text-xs sm:text-sm">
          {date} • {time}
        </p>
      </div>
    </div>
  );
};

export default ShowTile;
